import { useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useProductSelector } from "src/api/client-api/products/useProductSelector";
import { useGetOneProduct, type Product } from "src/api/client-api/products/useGetOneProduct";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { type SelectorProduct } from "src/api/client-api/products/productSchema";

import { ProductAutocomplete } from "./ProductAutocomplete";

interface FormProductSelectorProps {
    value: string;
    onChange: (value: string) => void;
    intermediateProducts?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: object;
}

const mapProductToSelector = (product: Product): SelectorProduct => ({
    "@id": product["@id"],
    id: product.id,
    title: product.title,
    clientId: product.clientId,
    brand:
        product.brand ?
            {
                "@id": product.brand["@id"],
                id: product.brand.id,
                name: product.brand.name,
            }
        :   undefined,
    productLine:
        product.productLine ?
            {
                "@id": product.productLine["@id"],
                id: product.productLine.id,
                name: product.productLine.name,
            }
        :   undefined,
    canModify: product.canModify,
    canDelete: product.canDelete,
});

const mergeProductOptions = (selected: SelectorProduct | null, products: SelectorProduct[]): SelectorProduct[] => {
    if (!selected) {
        return products;
    }

    if (products.some((product) => product.id === selected.id)) {
        return products;
    }

    return [selected, ...products];
};

export const FormProductSelector: FC<FormProductSelectorProps> = ({
    value,
    onChange,
    intermediateProducts = false,
    disabled = false,
    error = false,
    helperText,
    sx,
}) => {
    const { t } = useTranslation();
    const { delayedSearch, setSearch, resetSearch } = useDelayedSearch();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const [pickedProduct, setPickedProduct] = useState<SelectorProduct | null>(null);

    const { products, isLoading, isFetching, isQueryEnabled } = useProductSelector({
        searchText: delayedSearch,
        intermediateProducts,
        companyId: selectedCompany?.id,
        campaignId: currentCampaign?.id,
    });

    const selectedListProduct = products.find((product) => String(product.id) === value);
    const { product: selectedProduct } = useGetOneProduct(value, {
        campaignId: currentCampaign?.id,
        companyId: selectedCompany?.id,
        enabled: value !== "" && selectedListProduct === undefined && pickedProduct === null,
    });

    useEffect(() => {
        if (value === "") {
            setPickedProduct(null);
            return;
        }

        if (pickedProduct !== null && String(pickedProduct.id) !== value) {
            setPickedProduct(null);
        }
    }, [value, pickedProduct]);

    const selectedValue = useMemo((): SelectorProduct | null => {
        if (selectedListProduct) {
            return selectedListProduct;
        }

        if (pickedProduct !== null && String(pickedProduct.id) === value) {
            return pickedProduct;
        }

        if (selectedProduct) {
            return mapProductToSelector(selectedProduct);
        }

        return null;
    }, [selectedListProduct, pickedProduct, value, selectedProduct]);

    const options = useMemo(() => mergeProductOptions(selectedValue, products), [products, selectedValue]);

    const isLoadingOptions = isLoading || isFetching;
    const noOptionsText =
        !isQueryEnabled ? t("product.select.noCampaign", "Select a campaign year before choosing a product")
        : isLoadingOptions && products.length === 0 ? t("product.select.loading", "Loading...")
        : delayedSearch.trim() !== "" ? t("product.select.noResultFound", "No products found")
        : t("product.select.noOptions", "No products available");

    return (
        <ProductAutocomplete
            value={selectedValue}
            options={options}
            onChange={(product) => {
                setPickedProduct(product);
                onChange(product ? String(product.id) : "");
            }}
            onSearchChange={setSearch}
            onSearchReset={resetSearch}
            loading={isLoadingOptions}
            disabled={disabled || !isQueryEnabled}
            error={error}
            helperText={helperText}
            noOptionsText={noOptionsText}
            sx={sx}
        />
    );
};
